import { React, PT, WithData } from '@gasbuddy/react';
import moment from 'moment';
import BN from 'bignumber.js';
import DatePicker from 'react-datepicker';
import { Table, Label } from 'semantic-ui-react';
import { Auth, Nav } from '../../components';
import { Role } from '../../roles';

type Props = {
  history: {
    // eslint-disable-next-line react/no-unused-prop-types
    push: Function,
  },
  // eslint-disable-next-line react/require-default-props
  match?: {
    params?: {
      // eslint-disable-next-line react/no-unused-prop-types
      date?: string
    },
  },
};

function discrepancy(data) {
  if (!data) {
    return '-';
  }
  return `$${new BN(data.wex_settled_amount || 0)
    .plus(data.dwolla_settled_amount || 0)
    .plus(data.discount_amount || 0)
    .plus(data.customer_credit_applied_amount || 0)
    .neg()
    .toFormat(2)}`;
}

function payTransform(batches) {
  if (!batches) { return []; }
  const rows = [];
  batches.forEach((b) => {
    const payTotal = (b.payments || []).reduce((p, c) => p.plus(c.amount), new BN(0));
    rows.push({
      batch: b.batch,
      total: b.total,
      count: b.count,
      created_at: b.created_at,
      rowStyle: { [new BN(b.total).eq(payTotal) ? 'positive' : 'negative']: true },
    });
    if (b.payments) {
      rows.push(...b.payments.map((m) => { m.batchDate = moment(b.batch, 'YYYYMMDD'); return m; }));
    }
  });
  return rows;
}

function renderRow(r) {
  if (r.batch) {
    return (
      <Table.Row {...r.rowStyle}>
        <Table.Cell>
          <Label ribbon>
            {r.batch}
            {' '}
processed on
            {moment(r.created_at).format('dddd MM/DD hh:mm')}
          </Label>
        </Table.Cell>
        <Table.Cell colSpan="4" style={{ textAlign: 'right' }}>
          {new BN(r.count || 0).toFormat(0)}
          {' '}
/
          $
          {new BN(r.total || 0).toFormat(2)}
        </Table.Cell>
      </Table.Row>
    );
  }
  return (
    <Table.Row>
      <Table.Cell>{moment(r.created).format('MM/DD hh:mm')}</Table.Cell>
      <Table.Cell textAlign="center">{moment(r.created).diff(r.batchDate, 'days')}</Table.Cell>
      <Table.Cell textAlign="center">{moment(r.deposited).format('dddd MM/DD')}</Table.Cell>
      <Table.Cell textAlign="center">{moment(r.deposited).diff(r.batchDate, 'days')}</Table.Cell>
      <Table.Cell style={{ textAlign: 'right' }}>
$
        {new BN(r.amount).toFormat(2)}
      </Table.Cell>
    </Table.Row>
  );
}

const TableProxy = ({ data }) => {
  if (!data) {
    return null;
  }
  return (
    <div>
      <h2>Monthly Metrics</h2>
      <Table definition striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell />
            <Table.HeaderCell>Count</Table.HeaderCell>
            <Table.HeaderCell>Value</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          <Table.Row>
            <Table.Cell>Settled Wex Transactions</Table.Cell>
            <Table.Cell>{data.wex_settled_count || 0}</Table.Cell>
            <Table.Cell>{data.wex_settled_amount ? `$${new BN(data.wex_settled_amount).neg().toFormat(2)}` : '-'}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Settled Dwolla Transactions</Table.Cell>
            <Table.Cell>{data.dwolla_settled_count || 0}</Table.Cell>
            <Table.Cell>{data.dwolla_settled_amount ? `$${new BN(data.dwolla_settled_amount).neg().toFormat(2)}` : '-'}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>GasBuddy Discount</Table.Cell>
            <Table.Cell>{data.discount_count || 0}</Table.Cell>
            <Table.Cell>{data.discount_amount ? `$${new BN(data.discount_amount).neg().toFormat(2)}` : '-'}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>
ShopYourWay Discount
              {data.syw_discount_amount ? `($${new BN(data.syw_discount_amount).neg().toFormat(2)} to customers)` : ''}
            </Table.Cell>
            <Table.Cell>{data.syw_discount_count || 0}</Table.Cell>
            <Table.Cell>{data.syw_discount_amount ? `$${new BN(data.syw_discount_amount).neg().times('.25').toFormat(2)}` : '-'}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Account Credit Applied</Table.Cell>
            <Table.Cell>{data.customer_credit_applied_count || 0}</Table.Cell>
            <Table.Cell>{data.customer_credit_applied_amount ? `$${new BN(data.customer_credit_applied_amount).neg().toFormat(2)}` : '-'}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell textAlign="right">Discrepancy</Table.Cell>
            <Table.Cell />
            <Table.Cell>{discrepancy(data)}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Expected Wex Revenue</Table.Cell>
            <Table.Cell />
            <Table.Cell>{data.wex_settled_amount ? `$${new BN(data.wex_settled_amount).times('.0207').toFormat(2)}` : '-'}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Settled Dwolla Payments</Table.Cell>
            <Table.Cell>{data.dwolla_attempted_count || 0}</Table.Cell>
            <Table.Cell>{data.dwolla_attempted_amount ? `$${new BN(data.dwolla_attempted_amount).toFormat(2)}` : '-'}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Settled Dwolla Chargebacks</Table.Cell>
            <Table.Cell>{data.dwolla_failed_count || 0}</Table.Cell>
            <Table.Cell>{data.dwolla_failed_amount ? `$${new BN(data.dwolla_failed_amount).toFormat(2)}` : '-'}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Fees Assessed</Table.Cell>
            <Table.Cell />
            <Table.Cell>{data.fees_assessed ? `$${new BN(data.fees_assessed).toFormat(2)}` : '-'}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Losses Taken</Table.Cell>
            <Table.Cell />
            <Table.Cell>{data.losses ? `$${new BN(data.losses).neg().toFormat(2)}` : '-'}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell>Account Credits Given</Table.Cell>
            <Table.Cell />
            <Table.Cell>{data.credit_given ? `$${new BN(data.credit_given).toFormat(2)}` : '-'}</Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>

      <h2>Batch Payment Detail</h2>
      <Table>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={3}>Payment Date</Table.HeaderCell>
            <Table.HeaderCell textAlign="center" width={3}>Days To Transmittal</Table.HeaderCell>
            <Table.HeaderCell width={3}>Estimated Receipt Date</Table.HeaderCell>
            <Table.HeaderCell textAlign="center" width={3}>Days To Receipt</Table.HeaderCell>
            <Table.HeaderCell textAlign="right">Amount</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {payTransform(data.payments).map(renderRow)}
        </Table.Body>
      </Table>
    </div>
  );
};

TableProxy.propTypes = {
  data: PT.shape({
    wex_settled_count: PT.string,
  }),
};

TableProxy.defaultProps = {
  data: null,
};

export const Monthly = ({ history, match: {
  params: {
    date = moment().utcOffset('-04:00').subtract(1, 'month').format(),
  } = {},
} = {} }: Props) => (
  <Auth forRoles={[Role.Superuser, Role.PaymentView]}>
    <Nav>
      <div>
        <div style={{ textAlign: 'right', paddingRight: '25px' }}>
          Show
          {' '}
          <div className="inline-datepicker">
            <DatePicker
              selected={moment(date)}
              className="datetime"
              onChange={d => history.push(`/payments/monthly/${moment(d).format('YYYY-MM-DD')}`)}
            />
          </div>
        </div>
        <WithData url={`/payments/monthly/${moment(date).format('YYYYMM')}`}>
          <TableProxy />
        </WithData>
      </div>
    </Nav>
  </Auth>
);
